import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, input, linkedSignal, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Select } from 'primeng/select';
import { InputText } from 'primeng/inputtext';
import { InputNumber } from 'primeng/inputnumber';
import { Checkbox } from 'primeng/checkbox';
import { ContentItemsFormComponent } from '../content-items-form/content-items-form';
import { SectionCardComponent } from '../section-card/section-card';
import { MediaPickerComponent } from '../media-picker/media-picker';
import { FileCategory, JsonData } from '../../models';
import { EditorFilesService, type JobStatusResponse } from '../../../features/editor/services/editor-files.service';
import { EditorJobsService } from '../../../features/editor/services/editor-jobs.service';
import { EditorStateService } from '../../../features/editor/services/editor-state.service';
import type { ImageData } from '@webout/types';
import { cloneDeep } from '../../utils/clone';
import { humanizeEnum } from '../../utils/format';
import {
	clearedDimensions,
	dimensionsWarning,
	hasOverride,
	isPersistable,
	readDimensionsDraft,
	toRefPatch,
	unknownWidthWarning,
	withDimension,
	withOrigCoordsWidth,
	type DimensionsDraft,
	type DimensionsPatch,
	type ImageRefDimensions,
} from '../../utils/image-ref-dimensions';
import { imageGenerateBlocker } from '../../utils/image-generate';
import { AeComp } from '../../utils/ae-converter';

@Component({
	selector: 'app-image-form',
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [FormsModule, Select, InputText, InputNumber, Checkbox, ContentItemsFormComponent, SectionCardComponent, MediaPickerComponent],
	templateUrl: './image-form.html',
	styleUrl: './image-form.scss',
})
export class ImageFormComponent {
	 
	data = input.required<any>();
	dataChange = output<JsonData>();
	/**
	 * DEV-697: the image's entry in `project.json > images[]`, not its
	 * `image.json` document. The player reads render size only from here, so the
	 * editor plumbs the ref in and folds changes back into `projectData` —
	 * mirroring `scene-form`'s `sceneRefAttributes`.
	 */
	imageRef = input<ImageRefDimensions | null>(null);
	imageRefDimensionsChange = output<DimensionsPatch>();
	/** Widths the project generates, for the advisory under the Width field. */
	availableWidths = input<readonly number[]>([]);
	slugRenamed = output<{ oldSlug: string; newSlug: string }>();
	requestSave = output<void>();
	copyItem = output<JsonData>();
	/**
	 * DEV-655: render this one image and show it. The editor owns the dialog —
	 * it holds the project slug, the signature and the live args/vars a render
	 * needs, none of which this form has.
	 */
	previewRequest = output<void>();

	private filesService = inject(EditorFilesService);
	private jobsService = inject(EditorJobsService);
	private stateService = inject(EditorStateService);
	private destroyRef = inject(DestroyRef);
	imagePath = input<string>('');
	aeComp = input<AeComp>({ fps: 25, width: 1920, height: 1080 });
	protected pickerVisible = signal(false);
	protected pickerCategories = signal<FileCategory[]>([]);

	protected readonly srcTypeOptions = ['RELATIVE', 'ABSOLUTE', 'SHARED']
		.map(value => ({ label: humanizeEnum(value), value }));
	protected readonly bgDataTypeOptions = ['IMAGE', 'VIDEO']
		.map(value => ({ label: humanizeEnum(value), value }));

	protected fileName(url: string | undefined): string {
		if (!url) return '';
		if (url.startsWith('https://')) return url;
		const media = this.filesService.findByBaseUrl(url);
		return media?.name ?? url.split('/').pop() ?? url;
	}

	 
	protected patch(patcher: (draft: any) => void): void {
		const clone = cloneDeep(this.data() ?? {});
		patcher(clone);
		this.dataChange.emit(clone);
		const path = this.imagePath();
		if (path) this.stateService.setImage(path, clone as ImageData);
	}

	protected onSlugChange(newSlug: string): void {
		const oldSlug = this.data()?.slug;
		if (!newSlug?.trim() || newSlug === oldSlug) return;
		this.slugRenamed.emit({ oldSlug, newSlug: newSlug.trim() });
	}

	// ── Dimensions (on the REF, not on image.json) ──────────────────────────
	//
	// The three setters that used to live here wrote `dimensions` /
	// `origCoordsWidth` into `image.json`, which the player never reads — dead
	// code since the fields were dropped from the template. DEV-697 Phase 2.

	/**
	 * What the fields show. Seeded from the ref and re-seeded whenever a
	 * different image is opened, but editable ahead of the ref: while one axis
	 * is being retyped the pair is incomplete, and that state must stay local.
	 * Written to `project.json` it would be permanent — the backend's
	 * `usableDimensions()` accepts any finite number, so a 0 reads as a
	 * deliberate value and is never re-derived.
	 */
	protected draft = linkedSignal(() => readDimensionsDraft(this.imageRef()));
	protected hasDimensionsOverride = computed(() => hasOverride(this.draft()));
	protected sizeWarning = computed(() => dimensionsWarning(this.draft()));
	protected widthWarning = computed(() =>
		unknownWidthWarning(this.draft(), this.availableWidths())
	);

	/** Show every keystroke; write only a state the ref can hold. */
	private applyDraft(next: DimensionsDraft): void {
		this.draft.set(next);
		if (isPersistable(next)) this.imageRefDimensionsChange.emit(toRefPatch(next));
	}

	protected setRefDimension(field: 'width' | 'height', value: number | null): void {
		this.applyDraft(withDimension(this.draft(), field, value));
	}

	protected setRefOrigCoordsWidth(value: number | null): void {
		this.applyDraft(withOrigCoordsWidth(this.draft(), value));
	}

	/** Hand the ref back to the backend, which re-derives it on the next save. */
	protected resetRefDimensions(): void {
		this.applyDraft(clearedDimensions());
	}

	// ── Render preview (DEV-655) ────────────────────────────────────────────
	//
	// There is no image viewer anywhere else in the editor: the centre player is
	// mounted on chapters, so selecting an image only ever opened this form.
	// That is why the ticket's reporter had to create a whole separate project
	// to look at one 16:9 image inside a 9:16 one.

	/**
	 * Why a render is impossible for this image, or `null`.
	 *
	 * Checked here rather than in the dialog because every case makes the
	 * player's `generateImage()` hang instead of fail — an operator deserves
	 * the sentence up front, not a spinner that ends in a timeout.
	 */
	protected previewBlocker = computed(() => imageGenerateBlocker(this.imagePath(), this.imageRef()));

	/**
	 * The trigger is an anchor, not a button, so that an imported (read-only)
	 * project can still be looked at — see the template. That also means the
	 * blocked state has to be enforced here rather than by `disabled`.
	 *
	 * `preventDefault()` is load-bearing for the Space key: the editor's
	 * document-level handler toggles playback on Space unless the event is
	 * already default-prevented, and its `isTypingTarget()` escape hatch only
	 * covers real inputs. Without this, opening the dialog from the keyboard
	 * would start the video behind it.
	 */
	protected requestPreview(event?: Event): void {
		event?.preventDefault();
		if (this.previewBlocker()) return;
		this.previewRequest.emit();
	}

	protected openBgPicker(): void {
		const bgType = this.data()?.background?.source?.dataType;
		const hasMask = this.data()?.mask;
		if (bgType === 'VIDEO') {
			this.pickerCategories.set(hasMask ? ['video', 'video-mask'] : ['video']);
		} else {
			this.pickerCategories.set(['images']);
		}
		this.pickerVisible.set(true);
	}

	protected setBgSrc(value: string): void {
		this.patch(d => {
			if (!d.background) d.background = {};
			if (!d.background.source) d.background.source = {};
			d.background.source.src = value;
			d.background.source.srcType = 'ABSOLUTE';
		});
		const media = this.filesService.findByBaseUrl(value);
		const mediaId = media?.id;
		if (media?.isLocal && mediaId && (media.versionCount ?? 0) <= 1) {
			this.filesService.generateVersions(mediaId)
				.pipe(this.jobsService.andTrack({ mediaId, mediaName: media.name ?? '', destroyRef: this.destroyRef }))
				.subscribe();
		}
	}
	protected clearBgSrc(): void {
		this.patch(d => {
			// Remove the WHOLE background, not just `src`. Deleting `src` alone left
			// `{ srcType, dataType }` behind — a source the player dereferences
			// unguarded (`generateAbsFilePath(undefined).replace(...)`), which throws
			// and aborts init for the ENTIRE player. A background with no media isn't
			// a background. (Same fix as scene-form's clearBgSrc.)
			delete d.background;
		});
	}

	protected setBgField(field: string, value: string | boolean): void {
		this.patch(d => {
			if (!d.background) d.background = {};
			if (!d.background.source) d.background.source = {};
			d.background.source[field] = value;
		});
	}

	protected setMask(value: boolean): void {
		this.patch(d => {
			if (value) d.mask = true;
			else delete d.mask;
		});
	}

	protected onContentChange(content: JsonData[]): void {
		this.patch(d => { d.content = content; });
	}

	// ── Version generation ──────────────────────────────────────────────────
	protected currentBgMedia = computed(() => {
		const src = this.data()?.background?.source?.src;
		if (!src || typeof src !== 'string') return undefined;
		return this.filesService.findByBaseUrl(src);
	});

	protected canGenerate = computed(() => !!this.currentBgMedia()?.isLocal);
	protected hasVersions = computed(() => (this.currentBgMedia()?.versionCount ?? 0) > 1);

	protected generateStatus = computed<JobStatusResponse | null>(() => {
		const mediaId = this.currentBgMedia()?.id;
		if (!mediaId) return null;
		const job = this.jobsService.getJobForMedia(mediaId);
		if (!job) return null;
		return {
			jobId: job.jobId, type: job.type, mediaId: job.mediaId,
			status: job.status, progress: job.progress,
			currentStep: job.currentStep, completedVersions: job.completedVersions,
			totalVersions: job.totalVersions, error: job.error,
		};
	});

	protected startGenerate(): void {
		const media = this.currentBgMedia();
		const mediaId = media?.id;
		if (!mediaId) return;
		this.filesService.generateVersions(mediaId)
			.pipe(this.jobsService.andTrack({
				mediaId,
				mediaName: media?.name ?? '',
				mediaCategory: media?.category,
				destroyRef: this.destroyRef,
			}))
			.subscribe();
	}

	protected dismissGenerateStatus(): void {
		const mediaId = this.currentBgMedia()?.id;
		const job = mediaId ? this.jobsService.getJobForMedia(mediaId) : undefined;
		if (job) this.jobsService.dismissJob(job.jobId);
	}
}
